import * as React from "react";
import { ErrorBoundary } from "../../ErrorBoundary";
import { ChatMessage } from "../types";
import { usePlayerProfile } from "../../PlayerProfile";
import { ChatContainer } from "../ChatComponent";
import { loader } from "graphql.macro";
import { useMutation } from "../../../hooks/useMutation";
import {
  SendInstantTournamentChatMessageMutation,
  SendInstantTournamentChatMessageMutationVariables,
} from "../graphql/SendInstantTournamentMessageMutation.generated";
import {
  SendScheduledTournamentChatMessageMutation,
  SendScheduledTournamentChatMessageMutationVariables,
} from "../graphql/SendScheduledTournamentMessageMutation.generated";
import { useQuery } from "../../../hooks/useQuery";
import {
  PurchaseVirtualItemInChatMutation,
  PurchaseVirtualItemInChatMutationVariables,
} from "../../Chat/graphql/PurchaseVirtualItemInChat.generated";
import { SendGiftModal } from "../SendGift";
import { EditorStateProvider } from "../EditorState";
import classNames from "classnames";
import styles from "../Chat.module.scss";
import { Messages } from "../Messages";
import MessageInput from "../Input";
import { MAX_CHAT_MESSAGE_LENGTH } from "../../../config";
import { useChatMessages } from "../hooks/useChatMessages";
import { useCallback } from "react";
import { Editor } from "draft-js";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { Loader } from "../../Loader";
import { useUser } from "../../UserProvider";
import BlockedChats from "../BlockedChats";

const SEND_INSTANT_TOURNAMENT_MUTATION = loader(
  "../graphql/SendInstantTournamentMessageMutation.graphql"
);

const SEND_SCHEDULED_TOURNAMENT_MUTATION = loader(
  "../graphql/SendScheduledTournamentMessageMutation.graphql"
);

const GET_TOURNAMENT_MESSAGES_QUERY = loader(
  "../graphql/GetChatMessages.graphql"
);

const PURCHASE_GIFT_MUTATION = loader(
  "../../Chat/graphql/PurchaseVirtualItemInChat.graphql"
);

const MAX_CHAT_MESSAGES = 100;
let localMessageId = 0;
const MESSAGES_CONTAINER = { height: "100%", paddingBottom: "40px" };
const SCROLL_THRESHOLD = MESSAGES_CONTAINER.height;

const mergeMessages = (
  prev: Array<ChatMessage>,
  newMessages: Array<ChatMessage>
) => {
  const map = new Map<string, ChatMessage>();
  prev.forEach((m) => map.set(m.id, m));
  newMessages.forEach((m) => map.set(m.id, m));

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );
};

export function TournamentChat({
  id,
  type,
  updatedMessages,
}: {
  id: string;
  type: string;
  updatedMessages: Array<ChatMessage>;
}) {
  const {
    messages,
    fetchMessagesMore,
    chatMessageData,
    handleSetNewMessage,
    setCurrentId,
  } = useChatMessages();
  const {
    user: { chatBlocked },
  } = useUser();

  const showProfile = usePlayerProfile();
  const inputRef = React.useRef<Editor | null>(null);
  const messagesRef = React.useRef<HTMLDivElement | null>(null);
  const [showGiftSelector, setShowGiftSelector] = React.useState(false);

  React.useEffect(() => {
    if (id) {
      setCurrentId(id);
    }
  }, [id]);

  React.useEffect(() => {
    if (updatedMessages) {
      if (updatedMessages.length === 1) handleSetNewMessage(updatedMessages);
    }
  }, [updatedMessages]);

  const [, sendGift] = useMutation<
    PurchaseVirtualItemInChatMutation,
    PurchaseVirtualItemInChatMutationVariables
  >(PURCHASE_GIFT_MUTATION);

  const handleSendGiftWindow = (val: boolean) => {
    setShowGiftSelector(val);
  };

  const loadingRef = useInfiniteScroll<HTMLDivElement>(
    { root: messagesRef, rootMargin: "50px" },
    async () => {
      const endCursor = chatMessageData?.nextCursor ?? null;
      if (endCursor === null) return Promise.reject();
      return fetchMessagesMore(endCursor);
    }
  );

  const [{ fetching }, onInstantTournamentChat] = useMutation<
    SendInstantTournamentChatMessageMutation,
    SendInstantTournamentChatMessageMutationVariables
  >(SEND_INSTANT_TOURNAMENT_MUTATION);

  const [
    { fetching: scheduledFetching },
    onScheduledTournamentChat,
  ] = useMutation<
    SendScheduledTournamentChatMessageMutation,
    SendScheduledTournamentChatMessageMutationVariables
  >(SEND_SCHEDULED_TOURNAMENT_MUTATION);

  const onProfileClick = React.useCallback(
    (profile: { id: string; name: string }) => {
      //@ts-ignore
      showProfile({ ...profile });
      return true;
    },
    [showProfile]
  );

  const sendTournamentMessage = (message: string) => {
    if (type === "instant-tournament") {
      onInstantTournamentChat({
        input: { message: message, instantTournamentId: id },
      }).then((chatData) => {
        if (chatData?.data?.sendInstantTournamentChatMessage) {
          handleSetNewMessage([
            {
              id: chatData.data?.sendInstantTournamentChatMessage?.id!,
              player: {
                id: chatData.data?.sendInstantTournamentChatMessage?.player
                  ?.id!,
                name: chatData.data?.sendInstantTournamentChatMessage?.player
                  ?.name!,
              },
              created: chatData.data?.sendInstantTournamentChatMessage
                ?.created!,
              message: chatData.data?.sendInstantTournamentChatMessage
                ?.message!,
              isReported: false,
            },
          ]);
        }
      });
    } else {
      onScheduledTournamentChat({
        input: { message: message, scheduledTournamentId: id },
      }).then((chatData) => {
        if (chatData?.data?.sendScheduledTournamentChatMessage) {
          handleSetNewMessage([
            {
              id: chatData.data?.sendScheduledTournamentChatMessage?.id!,
              player: {
                id: chatData.data?.sendScheduledTournamentChatMessage?.player
                  ?.id!,
                name: chatData.data?.sendScheduledTournamentChatMessage?.player
                  ?.name!,
              },
              created: chatData.data?.sendScheduledTournamentChatMessage
                ?.created!,
              message: chatData.data?.sendScheduledTournamentChatMessage
                ?.message!,
              isReported: false,
            },
          ]);
        }
      });
    }
  };

  const onSendGift = useCallback(
    async ({ item, logo }: { item: string; logo: string }) => {
      const imgUrl = `${process.env.REACT_APP_API_HOST}/virtual-items/${logo}`;

      if (id) {
        await sendGift({ input: { chatId: id, item } }).then((data) => {
          sendTournamentMessage(imgUrl);
          setShowGiftSelector(false);
        });
      }
    },
    []
  );

  return (
    <ErrorBoundary fallback={null}>
      <React.Suspense fallback={null}>
        <div className="relative h-full">
          {showGiftSelector ? (
            <SendGiftModal
              name={""}
              close={() => setShowGiftSelector(false)}
              onSendGift={onSendGift}
            />
          ) : (
            <EditorStateProvider>
              <div
                style={{
                  background: "#1459cc",
                  borderRadius: "0 0 3px 3px",
                  height: "100%",
                  padding: "10px",
                }}
              >
                {chatBlocked ? (
                  <BlockedChats />
                ) : (
                  <>
                    <div
                      style={MESSAGES_CONTAINER}
                      ref={messagesRef}
                      className={classNames(
                        styles.scrollable,
                        "flex",
                        "flex-col-reverse",
                        "border",
                        "border-gray-300",
                        "bg-gray-100",
                        "overflow-x-hidden",
                        "overflow-y-auto"
                      )}
                    >
                      <Messages
                        container={messagesRef}
                        onProfileClick={onProfileClick}
                        currentUserId={id}
                        messages={messages || []}
                      />
                      {chatMessageData?.hasMore && (
                        <div
                          className="flex w-full justify-center items-center py-2"
                          ref={loadingRef}
                        >
                          <Loader description={""} />
                        </div>
                      )}
                    </div>
                    <div
                      className="px-2 py-1 rounded-b"
                      style={{
                        position: "absolute",
                        left: "14px",
                        bottom: "10px",
                        borderRadius: "20px",
                        width: "calc(100% - 28px)",
                      }}
                    >
                      <ErrorBoundary fallback={null}>
                        <MessageInput
                          maxLength={MAX_CHAT_MESSAGE_LENGTH}
                          ref={inputRef}
                          onInput={sendTournamentMessage}
                          onSendGiftOpen={handleSendGiftWindow}
                          isSendGiftOpen={showGiftSelector}
                        />
                      </ErrorBoundary>
                    </div>
                  </>
                )}
              </div>
            </EditorStateProvider>
          )}
        </div>
      </React.Suspense>
    </ErrorBoundary>
  );
}
